import { useQueries } from "@tanstack/react-query";

import { useMergeQuery } from "@citrine/client";
import type { Team } from "@citrine/resources/team";
import { TeamClient } from "@citrine/resources/team";
import type { TeamMember } from "@citrine/resources/team-member";
import { TeamMemberClient } from "@citrine/resources/team-member";
import { UserClient } from "@citrine/resources/user";
import { alphabetize } from "@citrine/utils/sorting";

export interface TeamWithActions extends Team {
  actions: TeamMember["actions"];
}

export function useFindAllTeamsWithActions() {
  const meQuery = UserClient.getInstance().useGetMe();
  const teamsQuery = TeamClient.getInstance().useFindAll();

  const myId = meQuery.data!.id;

  const myMembershipQueries = useQueries({
    queries:
      teamsQuery.data?.map((team) => {
        const teamMemberClient = TeamMemberClient.getInstance(team.id);
        return {
          queryKey: teamMemberClient.getModelCacheKey(myId),
          queryFn: () => teamMemberClient.get(myId),
        };
      }) ?? [],
  });

  return useMergeQuery(
    [teamsQuery, ...myMembershipQueries],
    ([teams, ...myMemberships]) => {
      const teamsWithActions = teams.map((team, teamIndex) => {
        const myMembership = myMemberships[teamIndex];
        return myMembership
          ? {
              ...team,
              actions: myMembership.actions,
            }
          : undefined;
      });

      return teamsWithActions
        .filter(Boolean)
        .sort(alphabetize<TeamWithActions>("name"));
    },
  );
}
